import React, { useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import { SelectInput, TextInput } from '@wix/cli-ui-kit';
import type { VariantOf } from 'variant';
import { fields, isType, match, variant } from 'variant';
import { useTranslation } from '@wix/cli-i18n';
import type { ActionSchema } from '@wix/cli-app-definitions';
import { useProjectModel } from '../../../../model.js';
import { DevCenterComponent } from '../../../../dev-center/components/dev-center-component.js';
import {
  composeValidations,
  useValidation,
  uuidValidation,
} from '../../../../dev-center/components/validations/index.js';
import { useWixCliAppBi } from '../../../../bi/index.js';

const OpenModalOption = variant({
  Component: fields<{ componentId: string }>(),
  Other: {},
});

type OpenModalOption = VariantOf<typeof OpenModalOption>;

export const OpenModal: FC<{
  onActionSelected: (action: ActionSchema) => void;
}> = ({ onActionSelected }) => {
  const bi = useWixCliAppBi();
  const { model } = useProjectModel();
  const { t } = useTranslation();
  const [componentId, setComponentId] = useState<string>();
  const [shouldUseCustomComponentId, setShouldUseCustomComponentId] =
    useState<boolean>(false);

  const dashboardModals = useMemo(
    () => model.components.filter(isType(DevCenterComponent.DashboardModal)),
    [model.components]
  );

  const validateComponentId = useValidation(
    composeValidations([
      [
        uuidValidation,
        t(
          'generate_command.dashboard_menu_plugin.what_to_do.open_modal.choose_component.other.component_id_invalid'
        ),
      ],
    ])
  );

  useEffect(() => {
    if (componentId) {
      onActionSelected({
        openModal: {
          componentId,
        },
      });
    }
  }, [componentId, onActionSelected]);

  return (
    <>
      <SelectInput<OpenModalOption>
        label={t(
          'generate_command.dashboard_menu_plugin.what_to_do.open_modal.choose_component'
        )}
        info={t(
          'generate_command.dashboard_menu_plugin.what_to_do.open_modal.choose_component.info'
        )}
        options={[
          ...dashboardModals.map(({ id, title }) => ({
            key: id,
            title,
            value: OpenModalOption.Component({ componentId: id }),
          })),
          {
            key: 'other',
            title: t(
              'generate_command.dashboard_menu_plugin.what_to_do.open_modal.choose_component.other'
            ),
            value: OpenModalOption.Other(),
          },
        ]}
        onSubmit={(option) => {
          bi.cliFlowStepAnswered({
            question:
              'generate_command.dashboard_menu_plugin.what_to_do.open_modal.choose_component',
            questionKey:
              'generate_command.dashboard_menu_plugin.what_to_do.open_modal.choose_component',
            answer: option.title,
          });
          return match(option.value, {
            Component: ({ componentId }) => setComponentId(componentId),
            Other: () => setShouldUseCustomComponentId(true),
          });
        }}
      />
      {shouldUseCustomComponentId && (
        <TextInput
          label={t(
            'generate_command.dashboard_menu_plugin.what_to_do.open_modal.choose_component.other.choose_component_id'
          )}
          onSubmit={(componentId) => {
            bi.cliFlowStepAnswered({
              question: t(
                'generate_command.dashboard_menu_plugin.what_to_do.navigate_to_page.choose_page.other.choose_page_id'
              ),
              questionKey:
                'generate_command.dashboard_menu_plugin.what_to_do.navigate_to_page.choose_page.other.choose_page_id',
              answer: componentId,
            });
            setComponentId(componentId);
          }}
          validate={validateComponentId}
        />
      )}
    </>
  );
};
